import dayjs, { Dayjs } from "dayjs";
import type Meeting from "@acto/taskpane/core/domain/Meeting";

// Backend times are timezone-naive local wall-clock, so instants are parsed local
// (`dayjs("YYYY-MM-DDTHH:mm")`) and compared against local `now`, never through the
// UTC-midnight day helpers, which only carry day identity.
const toInstant = (meeting: Meeting, time: string): Dayjs =>
  dayjs(`${meeting.scheduledDate.format("YYYY-MM-DD")}T${time}`);

interface PlacedMeeting {
  meeting: Meeting;
  start: Dayjs;
  end: Dayjs;
}

// An appointment carrying no end counts as over the moment it begins.
const place = (meeting: Meeting): PlacedMeeting | null => {
  if (!meeting.scheduledStartTime) return null;

  const start = toInstant(meeting, meeting.scheduledStartTime);
  const end = meeting.scheduledEndTime ? toInstant(meeting, meeting.scheduledEndTime) : start;
  return { meeting, start, end };
};

const byStartInstant = (a: PlacedMeeting, b: PlacedMeeting): number => a.start.diff(b.start);

const byStartTime = (a: Meeting, b: Meeting): number =>
  (a.scheduledStartTime ?? "").localeCompare(b.scheduledStartTime ?? "");

const placeAll = (dayMeetings: Meeting[]): PlacedMeeting[] =>
  dayMeetings
    .map(place)
    .filter((entry): entry is PlacedMeeting => entry != null)
    .sort(byStartInstant);

/**
 * The next appointment: the earliest one that has not ended yet, so a running day yields the
 * one still to come and a day that has not started yields its first. Null once every
 * appointment on the day is over. An appointment without a time has no place on the clock, so
 * it is never over and is picked when nothing else is there.
 */
export function pickNextMeeting(dayMeetings: Meeting[], now: Date = new Date()): Meeting | null {
  if (dayMeetings.length === 0) return null;

  const placed = placeAll(dayMeetings);
  if (placed.length === 0) return [...dayMeetings].sort(byStartTime)[0];

  return placed.find((entry) => !entry.end.isBefore(now))?.meeting ?? null;
}

/**
 * The appointment to measure a day against: {@link pickNextMeeting}, except on a day that is
 * entirely over, where it is the last appointment. Null only for a day with no appointments.
 */
export function pickFocusedMeeting(dayMeetings: Meeting[], now: Date = new Date()): Meeting | null {
  const next = pickNextMeeting(dayMeetings, now);
  if (next) return next;

  return placeAll(dayMeetings).at(-1)?.meeting ?? null;
}
